/* global axios */
import ApiClient from './ApiClient';

// Manutenção programada (massiva PREVISTA) + navegação da rede.
//
// A navegação (`caixas`/`clientes`) é AO VIVO no ERP — barata (o filtro por
// circuito roda no próprio IXC), mas ainda assim uma ida à rede do provedor:
// timeout maior que o default do app, menor que o do compute.
class AiNetworkMaintenances extends ApiClient {
  constructor() {
    super('ai/network_maintenances', { accountScoped: true });
  }

  scopedUrl(accountId) {
    return accountId
      ? `${this.apiVersion}/accounts/${accountId}/${this.resource}`
      : this.url;
  }

  topologyUrl(accountId) {
    return `${this.apiVersion}/accounts/${accountId}/ai/network_topology`;
  }

  // GET ai/network_topology/caixas?conexao=<circuito-do-erp>&system_label=<inst>
  // → { conexao, total, caixas: [{ caixa_ftth, olt, total, online, offline }] }
  // `system_label` é a DICA de instância (conta SGP com N hosts): sem ela o
  // backend cai na primeira instância — e numa conta multi-instância isso
  // navegaria a rede do ERP errado em silêncio. A RESPOSTA sempre carimba o
  // system_label de quem respondeu.
  // GET ai/network_topology/agrupadores — catálogo pro combobox (cacheado no
  // Go; `warming: true` = primeira colheita em curso, repetir depois).
  agrupadores(accountId, systemLabel) {
    return axios.get(`${this.topologyUrl(accountId)}/agrupadores`, {
      params: { system_label: systemLabel || undefined },
      timeout: 45000,
    });
  }

  caixasDaConexao(accountId, conexao, systemLabel) {
    return axios.get(`${this.topologyUrl(accountId)}/caixas`, {
      params: { conexao, system_label: systemLabel || undefined },
      timeout: 45000,
    });
  }

  // GET ai/network_topology/opcoes?tipo=<pop|nas|olt|oltpon|cto>&system_label=
  // Opções de UM tipo de alvo (seletor "tipos", fiel à tela do SGP). Cada `id`
  // é a CHAVE TIPADA (`pop:2`, `cto:MIH-615`) que vai no carrinho como
  // `caixa_ftth`; `contagem` vem dos censos (idade divulgada por colhido_em/
  // warming). Falha (503) NÃO é lista vazia: o store propaga e o componente
  // grita — "0 opções" leria como "provedor sem POP".
  opcoes(accountId, tipo, systemLabel) {
    return axios.get(`${this.topologyUrl(accountId)}/opcoes`, {
      params: { tipo, system_label: systemLabel || undefined },
      timeout: 45000,
    });
  }

  // GET ai/network_topology/clientes?caixa=<chave tipada>&system_label=
  // Clientes de um alvo (aceita TODAS as chaves, `pop:`/`nas:`/`olt:` inclusas —
  // respondem do censo LOCAL, rápido). Alimenta a hierarquia do Escopo
  // (alvo → clientes). Shape: { caixa_ftth, olt, clientes: [...] } + capabilities.
  clientes(accountId, caixa, systemLabel) {
    return axios.get(`${this.topologyUrl(accountId)}/clientes`, {
      params: { caixa, system_label: systemLabel || undefined },
      timeout: 45000,
    });
  }

  list(accountId) {
    return axios.get(this.scopedUrl(accountId));
  }

  // Cadastrar ENFILEIRA o congelamento do público (job) — e NÃO envia nada.
  // O timeout largo era do fan-out no ERP dentro da request; ele saiu daqui
  // (ver FreezeAudienceJob), mas o teto fica: a request ainda grava caixas e
  // faz o write-through da URA, e encurtar isso agora seria outra mudança.
  create(accountId, payload) {
    return axios.post(this.scopedUrl(accountId), payload, { timeout: 60000 });
  }

  update(accountId, id, payload) {
    return axios.patch(`${this.scopedUrl(accountId)}/${id}`, payload, {
      timeout: 60000,
    });
  }

  destroy(accountId, id) {
    return axios.delete(`${this.scopedUrl(accountId)}/${id}`);
  }

  // A prévia honesta: quem não tem telefone e quanto cabe no limite do canal.
  audience(accountId, id) {
    return axios.get(`${this.scopedUrl(accountId)}/${id}/audience`);
  }

  // ÚNICO ponto que manda mensagem pro cliente final. `placeholders` = mapa
  // placeholder do template → campo do cliente/conexão ({"1": "nome"}) — o
  // backend valida contra o corpo do template e recusa mapa furado (422).
  notify(
    accountId,
    id,
    { kind, templateName, templateLanguage, placeholders }
  ) {
    return axios.post(`${this.scopedUrl(accountId)}/${id}/notify`, {
      kind,
      template_name: templateName,
      template_language: templateLanguage,
      placeholders,
    });
  }

  reschedule(
    accountId,
    id,
    { endsAt, templateName, templateLanguage, placeholders }
  ) {
    return axios.post(`${this.scopedUrl(accountId)}/${id}/reschedule`, {
      ends_at: endsAt,
      template_name: templateName,
      template_language: templateLanguage,
      placeholders,
    });
  }

  // O texto que a IA usa com o afetado que escrever durante a janela (nada é
  // disparado). Endpoint próprio, FORA da trava do aviso.
  aiNotice(accountId, id, { text, mode }) {
    return axios.patch(`${this.scopedUrl(accountId)}/${id}/ai_notice`, {
      ai_notice_text: text,
      // elaborate = a IA reescreve; template = o texto sai EXATO (uma vez por
      // conversa). Ausente mantém o que está salvo.
      ...(mode ? { ai_notice_mode: mode } : {}),
    });
  }

  // Encerrar: a obra acabou — a IA deixa de responder NA HORA.
  finish(accountId, id) {
    return axios.post(`${this.scopedUrl(accountId)}/${id}/finish`);
  }

  // Recongelar o público: o congelamento é ASSÍNCRONO (job) — este POST só
  // volta a enfileirar. A manutenção que falhou NÃO é destruída, então o
  // operador decide quando tentar de novo (o backend recusa 422 se já está
  // congelando ou se o aviso já saiu). Devolve a manutenção (:show) com
  // `audience_status` de volta em `pending`.
  refreeze(accountId, id) {
    return axios.post(`${this.scopedUrl(accountId)}/${id}/refreeze`);
  }

  // Write-through da URA (SGP): reenvia a manutenção pro ERP. Devolve a
  // manutenção atualizada (mesmo shape do show, com `erp_sync`); 422 com
  // {error} quando o ERP recusa — o toast mostra a mensagem.
  resync(accountId, id) {
    return axios.post(`${this.scopedUrl(accountId)}/${id}/resync`);
  }
}

export default new AiNetworkMaintenances();
